import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';

function newRow() {
  return { key: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, minEur: '', maxEur: '', percentage: '' };
}

/**
 * Validation (matches backend):
 * 1) Min/max numeric; max blank = open-ended upper band.
 * 2) max >= min (reject e.g. 30–20).
 * 3) No duplicate exact same band (same min + same max / both open same min).
 * 4) No strict interior overlap: reject if maxA > minB AND maxB > minA (allows touching e.g. 1–20 then 20–30).
 * @param {Array<{ minEur?: string, maxEur?: string }>} rows
 * @returns {string|null} error message or null if OK
 */
function parseInclusiveRangeRow(r) {
  const min = String(r.minEur ?? '').trim();
  const max = String(r.maxEur ?? '').trim();
  if (min === '') return null;
  const minN = Number(min);
  if (!Number.isFinite(minN)) return null;
  if (max === '') return { min: minN, max: Infinity };
  const maxN = Number(max);
  if (!Number.isFinite(maxN) || maxN < minN) return null;
  return { min: minN, max: maxN };
}

/** True when intervals share more than a single boundary point (inclusive min, inclusive max). */
function strictInteriorOverlap(a, b) {
  return a.max > b.min && b.max > a.min;
}

export function getGuideFeeTierDuplicateError(rows) {
  if (!Array.isArray(rows)) return null;
  const seen = new Set();
  for (const r of rows) {
    const min = String(r.minEur ?? '').trim();
    const max = String(r.maxEur ?? '').trim();
    if (min === '') continue;
    const minN = Number(min);
    if (!Number.isFinite(minN)) continue;
    let key;
    if (max === '') {
      key = `${minN}|__open__`;
    } else {
      const maxN = Number(max);
      if (!Number.isFinite(maxN)) continue;
      if (maxN < minN) {
        return 'Maximum € cannot be less than minimum € for a guide fee range.';
      }
      key = `${minN}|${maxN}`;
    }
    if (seen.has(key)) {
      if (max === '') {
        return `Duplicate range: €${minN} with no upper limit appears more than once. Remove the extra row.`;
      }
      return `Duplicate range: €${minN}–€${max} appears more than once. Each min/max band must be unique.`;
    }
    seen.add(key);
  }

  const intervals = rows.map(parseInclusiveRangeRow).filter(Boolean);
  for (let i = 0; i < intervals.length; i += 1) {
    for (let j = i + 1; j < intervals.length; j += 1) {
      if (strictInteriorOverlap(intervals[i], intervals[j])) {
        return 'Two ranges overlap on more than one € value. Example: after 1–20, you cannot add 5–20. Touching bands like 1–20 and 20–30 are OK.';
      }
    }
  }

  return null;
}

const filledFieldSx = (colors, width) => ({
  width,
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& .MuiFilledInput-root': { backgroundColor: colors.primary[500] },
  '& .MuiFilledInput-root:hover': { backgroundColor: colors.primary[500] },
  '& .MuiFilledInput-root.Mui-focused': { backgroundColor: colors.primary[500] },
  input: { color: colors.grey[100] },
});

/**
 * @param {{ value: Array<{ key: string, minEur: string, maxEur: string, percentage: string }>, onChange: (v: typeof value) => void, colors: Record<string, Record<number, string>> }} props
 */
const GuideFeeTiersEditor = ({ value, onChange, colors }) => {
  const rows = Array.isArray(value) ? value : [];
  const duplicateError = getGuideFeeTierDuplicateError(rows);

  const addRow = () => onChange([...rows, newRow()]);
  const removeRow = (key) => onChange(rows.filter((r) => r.key !== key));
  const patchRow = (key, field, v) =>
    onChange(rows.map((r) => (r.key === key ? { ...r, [field]: v } : r)));

  const labelProps = { sx: { color: colors.grey[300] } };

  return (
    <Box display="grid" gap={1.5}>
      <Typography variant="caption" sx={{ color: colors.grey[100], lineHeight: 1.5 }}>
        Ranges use inclusive min and max (€ per billable passenger, based on average paid collections). Leave max blank
        for an open-ended upper band. If the average falls outside every range, the guide fee rate is 0%. You cannot
        repeat the same band; bands must not strictly overlap (touching at one €, e.g. 20, is OK).
      </Typography>
      {duplicateError && (
        <Typography variant="body2" sx={{ color: colors.redAccent[500], fontWeight: 600 }}>
          {duplicateError}
        </Typography>
      )}
      {rows.map((r) => (
        <Box key={r.key} display="flex" flexWrap="wrap" gap={1} alignItems="flex-start">
          <TextField
            size="small"
            variant="filled"
            type="number"
            label="Min €"
            value={r.minEur}
            onChange={(e) => patchRow(r.key, 'minEur', e.target.value)}
            inputProps={{ step: '0.01' }}
            InputLabelProps={labelProps}
            sx={filledFieldSx(colors, 120)}
          />
          <TextField
            size="small"
            variant="filled"
            type="number"
            label="Max € (optional)"
            value={r.maxEur}
            onChange={(e) => patchRow(r.key, 'maxEur', e.target.value)}
            inputProps={{ step: '0.01' }}
            InputLabelProps={labelProps}
            sx={filledFieldSx(colors, 140)}
          />
          <TextField
            size="small"
            variant="filled"
            type="number"
            label="Guide fee %"
            value={r.percentage}
            onChange={(e) => patchRow(r.key, 'percentage', e.target.value)}
            inputProps={{ step: '0.01', min: '0' }}
            InputLabelProps={labelProps}
            sx={filledFieldSx(colors, 130)}
          />
          <IconButton
            aria-label="remove range"
            onClick={() => removeRow(r.key)}
            sx={{
              mt: 0.5,
              color: colors.redAccent[500],
              '&:hover': { color: colors.redAccent[600], backgroundColor: `${colors.redAccent[500]}22` },
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}
      <Box>
        <Button
          type="button"
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          disabled={Boolean(duplicateError)}
          title={duplicateError || undefined}
          onClick={addRow}
          sx={{
            alignSelf: 'flex-start',
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': { backgroundColor: colors.blueAccent[800] },
          }}
        >
          Add range
        </Button>
      </Box>
    </Box>
  );
};

export default GuideFeeTiersEditor;
